import { getAccessToken } from "../../../util/tokenUtil";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL ?? "";
const BASE = `${API_BASE_URL}/api/bizchat/moment`;

type ApiResponse<T = unknown> = {
  code: string;
  data: T;
  message?: string;
};

function extractErrorMessage(text: string, fallback: string) {
  if (!text) return fallback;
  try {
    const json = JSON.parse(text) as ApiResponse<unknown>;
    if (json?.message) {
      return json.message;
    }
  } catch {
    // non-json response
  }
  return text;
}

async function request<T>(
  url: string,
  options: RequestInit = {}
): Promise<T> {
  const token = getAccessToken();
  const headers: HeadersInit = {
    "Content-Type": "application/json",
    Accept: "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(options.headers as Record<string, string>),
  };

  const res = await fetch(url, { ...options, headers });

  if (res.status === 401) {
    window.location.href = "/account/login";
    throw new Error("인증이 필요합니다.");
  }
  if (!res.ok) {
    const text = await res.text();
    throw new Error(extractErrorMessage(text, `요청 실패 (${res.status})`));
  }

  const json: ApiResponse<T> = await res.json();
  if (json.code !== "1000") {
    throw new Error(json.message ?? "서버 오류가 발생했습니다.");
  }
  return json.data;
}

// ─────────────────────────────────────────────
// 온보딩
// ─────────────────────────────────────────────

export function getMyAccountStatus() {
  return request<MomentAccountStatus | null>(`${BASE}/account/status`, {
    method: "GET",
  });
}

export function applyMoment(termsAgreed = "Y") {
  return request<MomentAccountStatus>(`${BASE}/account/apply`, {
    method: "POST",
    body: JSON.stringify({ termsAgreed }),
  });
}

export function registerChannel(channelSearchId: string) {
  const trimmedChannelSearchId = channelSearchId.trim();
  if (!trimmedChannelSearchId) {
    return Promise.reject(new Error("검색용 아이디를 입력해주세요."));
  }

  return request<MomentAccountStatus>(`${BASE}/account/channel`, {
    method: "POST",
    body: JSON.stringify({ channelSearchId: trimmedChannelSearchId }),
  });
}

// ─────────────────────────────────────────────
// 메시지 광고 운영
// ─────────────────────────────────────────────

export function saveMessageAd(payload: SaveMessageAdPayload) {
  return request<MomentAdDto>(`${BASE}/ads`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function listMomentImages(params: {
  page: number;
  size: number;
  sort?: "date" | "name";
  sizes?: [number, number][];
}) {
  const sizes = params.sizes?.map(([width, height]) => ({ width, height }));
  return request<MomentImageLibraryPage>(`${API_BASE_URL}/api/bizchat/image/list`, {
    method: "POST",
    body: JSON.stringify({ imgType: "MMT", sort: params.sort ?? "date", page: params.page, size: params.size, sizes }),
  });
}

export async function uploadMomentImage(file: File, name: string) {
  const token = getAccessToken();
  const headers: HeadersInit = {
    Accept: "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const formData = new FormData();
  formData.append("type", "MMT");
  formData.append("name", name);
  formData.append("file", file);

  const res = await fetch(`${API_BASE_URL}/api/bizchat/image/upload`, {
    method: "POST",
    headers,
    body: formData,
  });

  if (res.status === 401) {
    window.location.href = "/account/login";
    throw new Error("인증이 필요합니다.");
  }
  if (!res.ok) {
    const text = await res.text();
    throw new Error(extractErrorMessage(text, `이미지 업로드 실패 (${res.status})`));
  }

  const json: ApiResponse<MomentUploadedImageDto> = await res.json();
  if (json.code !== "1000") {
    throw new Error(json.message ?? "이미지 업로드 중 오류가 발생했습니다.");
  }
  return json.data;
}

export function updateMessageAd(kakaoAdId: string, payload: SaveMessageAdPayload) {
  return request<MomentAdDto>(`${BASE}/ads/${kakaoAdId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export function getDraftMessageAd(kakaoAdId: string) {
  return request<MomentAdDetailDto>(`${BASE}/ads/${kakaoAdId}/load`, {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export function getMessageAdDetail(kakaoAdId: string) {
  return request<MomentAdDetailDto>(`${BASE}/ads/${kakaoAdId}/detail`, {
    method: "GET",
  });
}

export function sendTestMessage(kakaoAdId: string, phone: string) {
  return request<unknown>(`${BASE}/ads/${kakaoAdId}/test`, {
    method: "POST",
    body: JSON.stringify({ phoneNumbers: [phone] }),
  });
}

export function getTargetingPrice(kakaoAdId: string, targeting?: TargetingOptions) {
  return request<TargetingPriceDto>(`${BASE}/ads/${kakaoAdId}/targeting-price`, {
    method: "POST",
    body: JSON.stringify(targeting ?? DEFAULT_TARGETING),
  });
}

export function createSendingReservation(
  kakaoAdId: string,
  sendDate: string,
  targetingType = "WHOLE"
) {
  return request<MomentAdDto>(`${BASE}/ads/${kakaoAdId}/reservation`, {
    method: "POST",
    body: JSON.stringify({ sendDate, targetingType }),
  });
}

export function getSendingReservation(kakaoAdId: string) {
  return request<unknown>(`${BASE}/ads/${kakaoAdId}/reservation`, {
    method: "GET",
  });
}

export function updateSendingReservation(kakaoAdId: string, sendDate: string) {
  return request<unknown>(`${BASE}/ads/${kakaoAdId}/reservation`, {
    method: "PATCH",
    body: JSON.stringify({ sendDate }),
  });
}

// ─────────────────────────────────────────────
// 메시지 광고 관리
// ─────────────────────────────────────────────

export function listMessageAds(params: {
  status?: string;
  adName?: string;
  messageType?: string;
  sendDateFrom?: string;
  sendDateTo?: string;
  createdDateFrom?: string;
  createdDateTo?: string;
  sortDirection?: "ASC" | "DESC";
  page?: number;
  size?: number;
}) {
  return request<MomentAdListDto>(`${BASE}/ads/list`, {
    method: "POST",
    body: JSON.stringify({ page: 0, size: 20, ...params }),
  });
}

// [ROLLBACK] 기존 DELETE 방식
// export function deleteMessageAd(kakaoAdId: string) {
//   return request<void>(`${BASE}/ads/${kakaoAdId}`, { method: "DELETE" });
// }

export function cancelAd(kakaoAdId: string) {
  return request<void>(`${BASE}/ads/${kakaoAdId}/cancel`, {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export function deleteMessageAd(kakaoAdId: string) {
  return request<void>(`${BASE}/ads/${kakaoAdId}/delete`, {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export function getReport(payload: ReportPayload) {
  return request<unknown>(`${BASE}/ads/reports`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function getStoredReport(kakaoAdId: string, datePreset: "YESTERDAY" | "LAST_7DAY" | "LAST_30DAY") {
  return request<StoredReportDto>(`${BASE}/ads/${kakaoAdId}/stored-report`, {
    method: "POST",
    body: JSON.stringify({ datePreset }),
  });
}

// ─────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────

export type MomentAccountStatus = {
  uid: number;
  kakaoAdAccountId: number | null;
  channelProfileId: string | null;
  channelName: string | null;
  channelSearchId: string | null;
  bizRightStatus: string | null;
  status: "PENDING" | "ACTIVE" | "INACTIVE";
  applyDate: string | null;
  activeDate: string | null;
  inviteAdminEmail?: string | null;
};

export type MomentAdDto = {
  uid: number;
  kakaoAdId: string;
  adName: string;
  messageType: string;
  status: string;
  audienceCnt: number | null;
  unitPrice: number;
  totalAmount: number | null;
  adminCancelReason?: string | null;
  sendDate: string | null;
  createdAt: string;
  updatedAt: string;
};

export type MomentAdDetailDto = MomentAdDto & {
  channelName?: string | null;
  adminCancelReason?: string | null;
  messageBody: Record<string, unknown>;
};

export type StoredReportDto = {
  datePreset: string;
  startDate: string;
  endDate: string;
  summary: {
    cost: number;
    msgSend: number;
    msgSendFail: number;
    msgOpen: number;
    msgClick: number;
    costPerMsgSend: string;
    costPerMsgOpen: string;
    costPerMsgClick: string;
    msgOpenRate: string;
    msgClickRate: string;
  };
  clickStats: Array<{
    key: string;
    count: number;
  }>;
  trend: Array<{
    date: string;
    msgSend: number;
    msgOpen: number;
    msgClick: number;
    msgOpenRate: string;
    msgClickRate: string;
  }>;
  dailyClickStats: Array<{
    date: string;
    key: string;
    count: number;
  }>;
};

export type MomentAdListDto = {
  list: MomentAdDto[];
  total: number;
  page: number;
  size: number;
};

export type TargetingPriceDto = {
  messageAdId: string | null;
  price: number;
  contractCount: number;
  channelFriendCount: number;
  totalBudget: number;
  totalBudgetWithVAT: number;
  cashAmount: number;
  freeAmount: number;
  availablePoint: number;
  remainingPointAfterDeduction: number;
  targetingType: "WHOLE" | "SMART" | "DIRECT";
};

export type MomentUploadedImageDto = {
  uid: number;
  imgType: string;
  imgId: string;
  imgName: string;
  imgUrl: string;
};

export type MomentImageLibraryItem = {
  uid: number;
  imgName: string;
  imgUrl: string;
  imgId: string | null;
  imgWidth: number | null;
  imgHeight: number | null;
};

export type MomentImageLibraryPage = {
  content: MomentImageLibraryItem[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
};

export type TargetingOptions = {
  deviceTypes: string[];
  type: "WHOLE" | "SMART" | "DIRECT";
  demographics: {
    genderType: "ALL" | "MALE" | "FEMALE";
    ageType: string;
    localType: string;
  };
};

export const DEFAULT_TARGETING: TargetingOptions = {
  deviceTypes: [],
  type: "WHOLE",
  demographics: {
    genderType: "ALL",
    ageType: "ALL",
    localType: "ALL",
  },
};

export type SaveMessageAdPayload = {
  adName: string;
  messageType: string;
  ageVerification: boolean;
  message: Record<string, unknown>;
};

export type ReportPayload = {
  messageAdId: string;
  datePreset?: string;
  start?: string;
  end?: string;
  metricsGroups?: string[];
};

// ─────────────────────────────────────────────
// 포인트
// ─────────────────────────────────────────────

export type PointInfo = {
  amount: number;
  freeAmount: number;
};

export type CurrentUserInfo = {
  phone: string | null;
};

/**
 * 현재 사용자의 포인트 잔액을 조회합니다.
 * 발송 예약 페이지에서 예약 후 포인트 뷰를 갱신할 때 사용합니다.
 *
 * 엔드포인트가 /moment 하위가 아닌 /user/info/message이므로
 * BASE 상수 대신 API_BASE_URL을 직접 사용합니다.
 */
export function getCurrentPointInfo(): Promise<PointInfo> {
  return request<PointInfo>(`${API_BASE_URL}/api/bizchat/user/info/message`, {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export function getCurrentUserInfo(): Promise<CurrentUserInfo> {
  return request<CurrentUserInfo>(`${API_BASE_URL}/api/bizchat/user/info`, {
    method: "POST",
    body: JSON.stringify({}),
  });
}
